import get from 'lodash.get';
import { constants, selectors } from '@yola/subscription-manager-js';
import { dialogs, integration } from '@yola/ws-sdk';
import user from 'yola-editor/src/js/modules/user';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import { snakeCaseToCamelCase } from 'src/js/modules/utils/convert-case';
import isDeveloperMode from 'yola-editor/src/js/modules/user/accessors/is-developer-mode';
import {
  PAGES_COUNT,
  MULTILINGUAL,
} from 'yola-editor/src/js/modules/upsell/constants/upsell-types';
import actionTypes from '../constants/action-types';

const {
  hostingPackageFeatureNames: {
    AI_BLOCK_GENERATION_QUOTA,
    AI_TEXT_ASSISTANT_QUOTA,
    AI_LOCALIZATION,
    PAGE_COUNT,
    SITE_LANGUAGES,
  },
} = constants;

const PAGES_COUNT_UPSELL_TYPE = snakeCaseToCamelCase(PAGES_COUNT);
// The multilingual limit and upsell live under different keys in editor settings.
const MULTILINGUAL_LIMIT_KEY = 'multilingualLimit';

const featureAvailabilityInterrupter = (store) => (next) => (action) => {
  if (action.type === actionTypes.REQUEST_FEATURE_AVAILABILITY) {
    const state = store.getState();

    let featureName = get(action.payload, 'featureName', '');
    const details = get(action.payload, 'details', {});
    const resolve = get(action.payload, 'resolve', Function.prototype);
    const reject = get(action.payload, 'reject', Function.prototype);

    // Allow bypass limits only with typing devMode = 1 in console
    if (isDeveloperMode()) {
      resolve();
      next(action);
      return;
    }

    if (featureName === PAGE_COUNT) {
      featureName = PAGES_COUNT_UPSELL_TYPE;
    }

    if (featureName === SITE_LANGUAGES) {
      featureName = MULTILINGUAL;
    }

    const limits = integration.selectors.getLimits(state);
    const upsells = integration.selectors.getUpsells(state);
    const targetUpsell = get(upsells, featureName);
    const targetLimit = get(
      limits,
      featureName === MULTILINGUAL ? MULTILINGUAL_LIMIT_KEY : featureName
    );

    if (!targetLimit || !targetUpsell) {
      resolve();
      next(action);
      return;
    }

    const { captions, premiumFeature, onUpgrade, purchaseFlowType } = targetUpsell;
    const availablePlatformComponents = user.selectors.getAvailablePlatformComponents(state);

    switch (featureName) {
      case AI_BLOCK_GENERATION_QUOTA:
      case AI_TEXT_ASSISTANT_QUOTA: {
        // b2c & reseller platform upgrade
        if (availablePlatformComponents.includes(user.platformComponents.PAYMENTS)) {
          onUpgrade(details).then(resolve, reject);
          return;
        }

        store.dispatch(
          dialogs.actions.show(dialogTypes.UPGRADE, {
            purchaseFlowType,
            premiumFeature,
            captions,
            onUpgrade,
            onUpgradeReject: reject,
            onUpgradeResolve: resolve,
            onClose: reject,
          })
        );

        break;
      }

      case PAGES_COUNT_UPSELL_TYPE: {
        if (details.count > targetLimit.amount) {
          const topPackage = selectors.getTopAvailableHostingPackage(state);
          const currentPackageType = selectors.getCurrentPackageType(state);

          if (topPackage.type === currentPackageType) {
            dialogs.operations.show(dialogTypes.PAGE_LIMIT_DIALOG, {
              pageLimit: targetLimit.amount,
              onAfterClose: reject,
            });
            break;
          }

          // b2c & reseller platform upgrade
          if (availablePlatformComponents.includes(user.platformComponents.PAYMENTS)) {
            onUpgrade(details).then(resolve, reject);
            return;
          }

          store.dispatch(
            dialogs.actions.show(dialogTypes.UPGRADE, {
              purchaseFlowType,
              premiumFeature,
              captions,
              onUpgrade,
              onUpgradeReject: reject,
              onUpgradeResolve: resolve,
              onClose: reject,
            })
          );
        } else {
          resolve();
        }

        break;
      }

      case MULTILINGUAL: {
        if (details.count > targetLimit.amount) {
          // b2c & reseller platform upgrade
          if (availablePlatformComponents.includes(user.platformComponents.PAYMENTS)) {
            onUpgrade(details).then(resolve, reject);
            return;
          }

          store.dispatch(
            dialogs.actions.show(dialogTypes.UPGRADE, {
              purchaseFlowType,
              premiumFeature,
              captions,
              onUpgrade,
              onUpgradeReject: reject,
              onUpgradeResolve: resolve,
              onClose: reject,
            })
          );
        } else {
          resolve();
        }

        break;
      }

      case AI_LOCALIZATION: {
        if (!targetLimit.available) {
          // b2c & reseller platform upgrade
          if (availablePlatformComponents.includes(user.platformComponents.PAYMENTS)) {
            onUpgrade(details).then(resolve, reject);
            return;
          }

          store.dispatch(
            dialogs.actions.show(dialogTypes.UPGRADE, {
              purchaseFlowType,
              premiumFeature,
              captions,
              onUpgrade,
              onUpgradeReject: reject,
              onUpgradeResolve: resolve,
              onClose: reject,
            })
          );
        } else {
          resolve();
        }

        break;
      }

      default: {
        if (!targetLimit.available) {
          // b2c & reseller platform upgrade
          if (availablePlatformComponents.includes(user.platformComponents.PAYMENTS)) {
            onUpgrade().then(resolve, reject);
            return;
          }

          store.dispatch(
            dialogs.actions.show(dialogTypes.UPGRADE, {
              purchaseFlowType,
              premiumFeature,
              captions,
              onUpgrade,
              onUpgradeReject: reject,
              onUpgradeResolve: resolve,
              onClose: reject,
            })
          );
        } else {
          resolve();
        }
      }
    }

    next(action);
    return;
  }

  next(action);
};

export default featureAvailabilityInterrupter;
